import React from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import { ownerApi } from "../../../../api/owner";
import { formatApiError } from "../../../../api/client";
import OwnerProjectStepsPanel from "./OwnerProjectStepsPanel";
import OwnerProjectTaskDialog from "./OwnerProjectTaskDialog";
import OwnerCommitmentTimeDialog from "../center/OwnerCommitmentTimeDialog";

function idOf(value) {
  return String(value?.id || value?._id || value || "");
}

function notifyOwnerMutation() {
  try {
    window.dispatchEvent(new CustomEvent("owner:jarvis-mutated", {
      detail: { scopes: ["today", "commitments", "projects", "waiting", "calendar"] },
    }));
  } catch {
    // CustomEvent es una mejora de sincronización; el modal ya recarga su propio contexto.
  }
}

export default function OwnerProjectPendingsDialog({ open, initiativeId, projectName = "", onClose, onOpenProject }) {
  const [context, setContext] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [activityOpen, setActivityOpen] = React.useState(false);
  const [editingTask, setEditingTask] = React.useState(null);
  const [timeCommitment, setTimeCommitment] = React.useState(null);

  const load = React.useCallback(async ({ silent = false } = {}) => {
    if (!open || !initiativeId) return;
    if (!silent) setLoading(true);
    setError("");
    try {
      const response = await ownerApi.initiativeContext(initiativeId);
      setContext(response?.context || null);
    } catch (err) {
      setContext(null);
      setError(formatApiError(err, "No pude cargar los pendientes de este proyecto."));
    } finally {
      if (!silent) setLoading(false);
    }
  }, [initiativeId, open]);

  React.useEffect(() => {
    if (!open) {
      setActivityOpen(false);
      setEditingTask(null);
      setTimeCommitment(null);
      return;
    }
    load();
  }, [load, open]);

  const refreshAfterMutation = React.useCallback(async () => {
    await load({ silent: true });
    notifyOwnerMutation();
  }, [load]);

  const initiative = context?.initiative || (initiativeId ? { id: initiativeId, name: projectName } : null);
  const steps = context?.steps || { items: [] };
  const taskRows = context?.steps?.items || context?.commitments?.items || [];
  const openRows = (context?.steps?.items || []).filter((row) => row?.status === "open");
  const pendingCount = openRows.length;
  const scheduledCount = openRows.filter((row) => row?.scheduledStartAt).length;
  const unscheduledCount = openRows.filter((row) => !row?.scheduledStartAt).length;
  const isStandby = String(context?.initiative?.status || "") === "paused";

  const openNew = () => {
    setEditingTask(null);
    setActivityOpen(true);
  };

  const closeTask = () => {
    setActivityOpen(false);
    setEditingTask(null);
  };

  return <>
    <Dialog
      open={Boolean(open)}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      scroll="paper"
      PaperProps={{ sx: { maxHeight: "min(88dvh, 900px)" } }}
    >
      <DialogTitle sx={{ pb: .7 }}>
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ fontWeight: 1000, fontSize: { xs: "1.05rem", sm: "1.25rem" } }}>
              Pendientes · {context?.initiative?.name || projectName || "Proyecto"}
            </Typography>
            <Typography sx={{ mt: .18, color: "text.secondary", fontSize: ".75rem" }}>
              {loading ? "Leyendo el proyecto…" : `${pendingCount} abiertos · ${unscheduledCount} sin agenda · ${scheduledCount} agendados. El proyecto guarda todo; sólo lo que elegís ocupa tiempo.`}
            </Typography>
          </Box>
          {initiativeId ? <Button
            size="small"
            variant="outlined"
            startIcon={<OpenInNewRoundedIcon />}
            onClick={() => onOpenProject?.(initiativeId)}
            sx={{ display: { xs: "none", sm: "inline-flex" }, textTransform: "none", fontWeight: 900, flexShrink: 0 }}
          >Abrir ficha</Button> : null}
          <IconButton aria-label="Cerrar" onClick={onClose} size="small"><CloseRoundedIcon /></IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 1 }}>
        {loading ? <Stack alignItems="center" justifyContent="center" spacing={1} sx={{ minHeight: 220 }}>
          <CircularProgress size={28} />
          <Typography sx={{ color: "text.secondary", fontSize: ".75rem" }}>Cargando pendientes…</Typography>
        </Stack> : null}

        {!loading && error ? <Alert severity="error" action={<Button size="small" onClick={() => load()} sx={{ textTransform: "none", fontWeight: 900 }}>Reintentar</Button>}>{error}</Alert> : null}

        {!loading && !error && isStandby ? <Alert severity="info" sx={{ mb: 1 }}>
          Stand by: conservo pendientes, ideas e historial, pero Jarvis no trae este proyecto a Today ni a sus rondas automáticas hasta que lo reactives.
        </Alert> : null}

        {!loading && !error && context ? <OwnerProjectStepsPanel
          key={initiativeId}
          steps={steps}
          onCreateSteps={async (rows) => {
            await ownerApi.createProjectSteps(initiativeId, rows);
            await refreshAfterMutation();
          }}
          onReorderSteps={async (stepIds) => {
            await ownerApi.reorderProjectSteps(initiativeId, stepIds);
            await refreshAfterMutation();
          }}
          onDeleteStep={async (step) => {
            await ownerApi.deleteProjectStep(initiativeId, idOf(step));
            await refreshAfterMutation();
          }}
          onEdit={(task) => {
            setEditingTask(task);
            setActivityOpen(true);
          }}
          onDone={async (task) => {
            if (!idOf(task)) return;
            try {
              await ownerApi.updateCommitment(idOf(task), { status: "done" });
              await refreshAfterMutation();
            } catch (err) {
              setError(formatApiError(err, "No pude completar ese pendiente."));
            }
          }}
          onState={async (task, state) => {
            if (!idOf(task)) return;
            try {
              await ownerApi.updateCommitment(idOf(task), {
                work: { ...(task?.work || {}), state, waitingFor: "", reviewAt: null },
              });
              await refreshAfterMutation();
            } catch (err) {
              setError(formatApiError(err, "No pude cambiar el estado de ese pendiente."));
            }
          }}
          onTime={setTimeCommitment}
          onNewActivity={openNew}
        /> : null}

        {!loading && !error && !context ? <Box sx={{ py: 4, textAlign: "center" }}>
          <Typography sx={{ color: "text.secondary", fontSize: ".8rem" }}>No pude encontrar el proyecto.</Typography>
        </Box> : null}

        {initiativeId ? <Button
          size="small"
          variant="text"
          startIcon={<OpenInNewRoundedIcon />}
          onClick={() => onOpenProject?.(initiativeId)}
          sx={{ display: { xs: "inline-flex", sm: "none" }, mt: 1.2, px: .2, textTransform: "none", fontWeight: 900 }}
        >Abrir ficha completa</Button> : null}
      </DialogContent>
    </Dialog>

    <OwnerProjectTaskDialog
      open={activityOpen}
      onClose={closeTask}
      onSaved={refreshAfterMutation}
      initiative={initiative}
      task={editingTask}
      tasks={taskRows}
    />

    <OwnerCommitmentTimeDialog
      open={Boolean(timeCommitment)}
      commitment={timeCommitment}
      onClose={() => setTimeCommitment(null)}
      onSaved={refreshAfterMutation}
    />
  </>;
}
